"use client";

import Image from "next/image";
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Link } from "@/i18n/navigation";
import Fragments from "../../ui/Gamification/Fragments/Fragments";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { useTranslations, useLocale } from "next-intl";
import useXpBoosters from "@/hooks/useXpBoosters";

const badgeColors = {
    gray: "bg-gray-100 text-gray-600 dark:bg-grayDark-800 dark:text-grayDark-400",
    green: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    amber: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
};

const InfoButton = ({ onClick, label }) => (
    <button
        onClick={onClick}
        className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-gray-100 dark:bg-grayDark-800 hover:bg-gray-200 dark:hover:bg-grayDark-700 transition-colors flex-shrink-0"
        title={label}
    >
        <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-500 dark:text-grayDark-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    </button>
);

const PricePill = ({ price, formatPrice }) => (
    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-bgDark-500 rounded-full">
        <Image src="/static/icons/fragment.svg" width={14} height={14} alt="Fragment" className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        <span className="font-bold text-gray-900 dark:text-white text-sm sm:text-base">
            {formatPrice(price)}
        </span>
    </div>
);

const BuyButton = ({ onClick, isLoading, children, size = "md" }) => {
    const sizeClass = size === "sm"
        ? "px-4 py-2 text-xs"
        : "px-5 py-2.5 text-sm";
    return (
        <button
            onClick={onClick}
            disabled={isLoading}
            className={`inline-flex items-center gap-2 ${sizeClass} bg-primary !text-white rounded-full font-semibold btn-3d lip-orange transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            {children}
        </button>
    );
};

// Featured Product Card - horizontal full-width (Premium, Protection, Exclusive)
const ProductCard = ({ icon, title, description, price, onBuy, isLoading, t, formatPrice, badge, badgeColor = "gray", shopImage, onInfoClick, note }) => (
    <div className="group relative rounded-2xl border border-gray-200 dark:border-bgDark-300 bg-white dark:bg-bgDark-840 p-5 sm:p-6 hover:border-primary dark:hover:border-primary hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Image src={icon} width={40} height={40} alt={title} className="w-9 h-9 sm:w-10 sm:h-10 object-contain" />
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                    <h3 className="font-bold font-sourceSans text-gray-900 dark:text-white text-base sm:text-lg">
                        {title}
                    </h3>
                    {badge && (
                        <span className={`px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-bold uppercase ${badgeColors[badgeColor]}`}>
                            {badge}
                        </span>
                    )}
                    {shopImage && (
                        <InfoButton onClick={() => onInfoClick(shopImage, title)} label={t("fragments_shop_more_info")} />
                    )}
                </div>
                <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                    {description}
                </p>
                {/* Ce qu'on possède déjà : la question qui vient juste avant
                    celle d'en racheter un. */}
                {note && (
                    <p className="mb-0 mt-1.5 text-sm font-semibold text-amber-600 dark:text-amber-400">
                        {note}
                    </p>
                )}
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                <PricePill price={price} formatPrice={formatPrice} />
                <BuyButton onClick={onBuy} isLoading={isLoading}>
                    {t("fragments_button_buy")}
                </BuyButton>
            </div>
        </div>

        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
    </div>
);

// Compact Product Card - vertical, used in goodies grid
const ProductCardCompact = ({ icon, title, description, price, onBuy, isLoading, t, formatPrice, shopImage, onInfoClick }) => (
    <div className="group relative rounded-2xl border border-gray-200 dark:border-bgDark-300 bg-white dark:bg-bgDark-840 p-5 sm:p-6 hover:border-primary dark:hover:border-primary hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden flex flex-col">
        <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
            <Image src={icon} width={36} height={36} alt={title} className="w-9 h-9 object-contain" />
        </div>

        <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold font-sourceSans text-gray-900 dark:text-white text-base sm:text-lg">
                {title}
            </h3>
            {shopImage && (
                <InfoButton onClick={() => onInfoClick(shopImage, title)} label={t("fragments_shop_more_info")} />
            )}
        </div>
        <p className="text-sm text-gray-500 dark:text-grayDark-400 line-clamp-3 mb-5 flex-1">
            {description}
        </p>

        <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-100 dark:border-bgDark-300">
            <PricePill price={price} formatPrice={formatPrice} />
            <BuyButton onClick={onBuy} isLoading={isLoading} size="sm">
                {t("fragments_button_buy")}
            </BuyButton>
        </div>

        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary to-secondary scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
    </div>
);

// Section Title (icon badge + heading, aligned on Leagues / Reading List)
const SectionTitle = ({ icon, children }) => (
    <div className="mb-5 sm:mb-6 flex items-center gap-3 sm:gap-4">
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <SolarIcon name={icon} className="w-5 h-5 text-primary" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold font-sourceSans tracking-tight text-gray-900 dark:text-white leading-tight">
            {children}
        </h2>
    </div>
);

// Modal (Responsive)
const Modal = ({ isOpen, onClose, children }) => (
    <AnimatePresence>
        {isOpen && (
            <div className="fixed inset-0 z-[100000] flex items-end sm:items-center justify-center p-0 sm:p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/50"
                />
                <motion.div
                    initial={{ opacity: 0, y: 100 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 100 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="relative w-full sm:max-w-xl bg-white dark:bg-bgDark-300 rounded-t-2xl sm:rounded-2xl shadow-xl overflow-hidden max-h-[85vh] sm:max-h-[90vh] overflow-y-auto"
                >
                    <button
                        onClick={onClose}
                        className="absolute top-3 sm:top-4 right-3 sm:right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-grayDark-800 transition-colors z-10"
                    >
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                    {children}
                </motion.div>
            </div>
        )}
    </AnimatePresence>
);

export default function FragmentsPage() {
    const t = useTranslations();
    // Ce qu'on a déjà en main : on achète mieux en sachant ce qui dort dans la
    // réserve, et c'est le rappel le plus naturel de l'endroit où l'activer.
    const { active: activeBooster, inventory: boosterInventory } =
        useXpBoosters();
    const locale = useLocale();
    const { data: session } = useSession();

    const formatPrice = (price) => Number(price).toLocaleString(locale);

    const [isExplanationOpen, setIsExplanationOpen] = useState(false);
    const [isBuying, setIsBuying] = useState(false);
    const [isInvitationOpen, setIsInvitationOpen] = useState(false);
    const [productImageModal, setProductImageModal] = useState({ isOpen: false, image: null, title: null });

    const openProductImageModal = (image, title) => {
        setProductImageModal({ isOpen: true, image, title });
    };

    const closeProductImageModal = () => {
        setProductImageModal({ isOpen: false, image: null, title: null });
    };

    // Buy handlers
    const buyShield = async () => {
        if (!session) { toast.error(t("fragments_shop_login_to_buy_error")); return; }
        setIsBuying(true);
        if (session.user.fragments < process.env.SHOP_PRICE_SHIELD) {
            setIsBuying(false);
            toast.error(t("fragments_shop_not_enough_fragments"));
            return;
        }
        const response = await fetch("/api/user/post/buy-shield", { method: "post", headers: { "Content-Type": "application/json" } });
        const fetchedData = await response.json();
        setIsBuying(false);
        if (!response.ok) { toast.error(fetchedData.message); }
        else {
            toast.success(t("fragments_shop_buy_shield_success"), {
                icon: () => <Image src="/static/icons/shield.svg" width={24} height={24} alt="Shield" />,
            });
            document.dispatchEvent(new Event("visibilitychange"));
        }
    };

    // Booster d'XP : même mécanique que le bouclier, à une nuance près. Le
    // booster ne s'active pas à l'achat, il rejoint l'inventaire (c'est le back
    // qui en décide, comme pour les drops de coffres) : le message de succès
    // doit donc dire où il est parti, sinon on le cherche.
    const buyBooster = async () => {
        if (!session) { toast.error(t("fragments_shop_login_to_buy_error")); return; }
        setIsBuying(true);
        if (session.user.fragments < process.env.SHOP_PRICE_BOOSTER) {
            setIsBuying(false);
            toast.error(t("fragments_shop_not_enough_fragments"));
            return;
        }
        const response = await fetch("/api/reps/boosters-buy", { method: "post", headers: { "Content-Type": "application/json" } });
        const fetchedData = await response.json();
        setIsBuying(false);
        if (!response.ok) { toast.error(fetchedData.message); }
        else {
            toast.success(t("fragments_shop_buy_booster_success"), {
                icon: () => <Image src="/static/icons/boost.svg" width={16} height={24} alt="Booster" />,
            });
            // Rafraîchit le solde de fragments du header (même signal que le
            // bouclier).
            document.dispatchEvent(new Event("visibilitychange"));
            // Et fait apparaître le booster dans la barre du haut, tout de
            // suite : c'est la réponse à "je viens d'en acheter un, il est
            // où ?". Sans ce signal, il n'apparaissait qu'au changement de
            // page, ce qui donnait l'impression d'avoir payé pour rien.
            window.dispatchEvent(new Event("reps:boosters-changed"));
        }
    };

    const buyPremium = async () => {
        if (!session) { toast.error(t("fragments_shop_login_to_buy_error")); return; }
        setIsBuying(true);
        if (session.user.fragments < process.env.SHOP_PRICE_PREMIUM) {
            setIsBuying(false);
            toast.error(t("fragments_shop_not_enough_fragments"));
            return;
        }
        toast.info(t("fragments_shop_emailToBuy_description"), {
            icon: () => <Image src="/static/icons/ticket.svg" width={19} height={19} alt="Ticket" />,
        });
        setIsBuying(false);
    };

    const buyItem = async (itemPrice, itemIcon) => {
        if (!session) { toast.error(t("fragments_shop_login_to_buy_error")); return; }
        setIsBuying(true);
        if (session.user.fragments < itemPrice) {
            setIsBuying(false);
            toast.error(t("fragments_shop_not_enough_fragments"));
            return;
        }
        toast.info(t("fragments_shop_emailToBuy_description"), {
            icon: () => <Image src={itemIcon} width={19} height={19} alt="Item" />,
        });
        setIsBuying(false);
    };

    return (
        <>
            {/* Explanation Modal */}
            <Modal isOpen={isExplanationOpen} onClose={() => setIsExplanationOpen(false)}>
                <div className="p-5 sm:p-6 md:p-8 pt-12 sm:pt-6 md:pt-8">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                        {t("fragments_modale_title")}
                    </h2>
                    <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600 dark:text-grayDark-300 mb-4 sm:mb-6">
                        <p>{t("fragments_modale_description1")}</p>
                        <p>{t("fragments_modale_description2")}</p>
                        <p>{t("fragments_modale_description3")}</p>
                        <p>
                            <Link href="/writer" className="text-primary hover:underline">
                                {t("fragments_modale_description4")}
                            </Link>
                        </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-bgDark-500 rounded-lg sm:rounded-xl p-4 sm:p-5">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3 text-sm sm:text-base">
                            {t("fragments_modale_howToEarnFragments_title")}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-grayDark-300 mb-2 sm:mb-3">
                            {t("fragments_modale_howToEarnFragments_description")}
                        </p>
                        <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600 dark:text-grayDark-300">
                            <li className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                {t("fragments_modale_howToEarnFragments_1")}
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                {t("fragments_modale_howToEarnFragments_2")}
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                {t("fragments_modale_howToEarnFragments_3")}
                            </li>
                        </ul>
                    </div>
                </div>
            </Modal>

            {/* Invitation Modal */}
            <Modal isOpen={isInvitationOpen} onClose={() => setIsInvitationOpen(false)}>
                <div className="p-5 sm:p-6 md:p-8 pt-12 sm:pt-6 md:pt-8">
                    <span className="inline-block px-2 py-0.5 rounded text-[10px] sm:text-xs font-bold uppercase bg-amber-100 dark:bg-amber-800/30 text-amber-600 dark:text-amber-400 mb-2 sm:mb-3">
                        {t("fragments_badge_exclusive")}
                    </span>
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                        {t("fragments_modale_invitation_title")}
                    </h2>
                    <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-600 dark:text-grayDark-300 mb-4 sm:mb-6">
                        <p>{t("fragments_modale_invitation_description1")}</p>
                        <p>{t("fragments_modale_invitation_description2")}</p>
                        <p>{t("fragments_modale_invitation_description3")}</p>
                    </div>
                    <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg sm:rounded-xl p-4 sm:p-5 border border-amber-100 dark:border-amber-800/20">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2 sm:mb-3 text-sm sm:text-base">
                            {t("fragments_modale_invitation_details_title")}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-grayDark-300 mb-2 sm:mb-3">
                            {t("fragments_modale_invitation_details_description")}
                        </p>
                        <ul className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-gray-600 dark:text-grayDark-300">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <li key={i} className="flex items-start gap-2">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                    {t(`fragments_modale_invitation_details_${i}`)}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </Modal>

            {/* Product Image Modal */}
            <Modal isOpen={productImageModal.isOpen} onClose={closeProductImageModal}>
                <div className="p-5 sm:p-6 md:p-8 pt-12 sm:pt-6 md:pt-8">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 text-center">
                        {productImageModal.title}
                    </h2>
                    {productImageModal.image && (
                        <div className="w-full max-w-md mx-auto rounded-xl overflow-hidden">
                            <Image
                                src={productImageModal.image}
                                width={448}
                                height={448}
                                alt={productImageModal.title || "Product"}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                    )}
                </div>
            </Modal>

            {/* Main Layout */}
            <div className="flex xl:flex-row flex-col gap-6 sm:gap-8">
                {/* Main Content */}
                <div className="flex-1">
                    {/* Hero header */}
                    <header className="mb-10 sm:mb-14">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wider mb-5">
                            <SolarIcon name="shop-2-bold-duotone" className="w-3.5 h-3.5" />
                            {t("fragments_eyebrow")}
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-sourceSans tracking-tight text-gray-900 dark:text-white leading-[1.05]">
                            {t("fragments_title")}
                        </h1>
                        <p className="text-base sm:text-lg text-gray-600 dark:text-lightBlue/80 mt-5 mb-0 max-w-2xl">
                            {t("fragments_headline")}
                        </p>
                        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-bgDark-300 flex items-center gap-2">
                            <button
                                onClick={() => setIsExplanationOpen(true)}
                                className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-grayDark-300 hover:text-primary dark:hover:text-primary transition-colors"
                            >
                                <SolarIcon name="info-circle-bold-duotone" className="w-4 h-4 text-primary" />
                                {t("fragments_explanation")}
                            </button>
                        </div>
                    </header>

                    {/* Premium Section */}
                    <section className="mb-6 sm:mb-8">
                        <SectionTitle icon="crown-star-bold-duotone">{t("fragments_section_premium")}</SectionTitle>
                        <ProductCard
                            icon="/static/icons/ticket.svg"
                            title={t("fragments_shop_premium_title")}
                            description={t("fragments_shop_premium_description")}
                            price={process.env.SHOP_PRICE_PREMIUM}
                            onBuy={buyPremium}
                            isLoading={isBuying}
                            t={t}
                            formatPrice={formatPrice}
                            badge={t("fragments_badge_popular")}
                            badgeColor="green"
                            shopImage="/images/site/assets/shop/shop-premium.jpg"
                            onInfoClick={openProductImageModal}
                        />
                    </section>

                    {/* Protection Section */}
                    <section className="mb-6 sm:mb-8">
                        <SectionTitle icon="shield-keyhole-bold-duotone">{t("fragments_section_protection")}</SectionTitle>
                        <ProductCard
                            icon="/static/icons/shield.svg"
                            title={t("fragments_shop_shield_title")}
                            description={t("fragments_shop_shield_description")}
                            price={process.env.SHOP_PRICE_SHIELD}
                            onBuy={buyShield}
                            isLoading={isBuying}
                            t={t}
                            formatPrice={formatPrice}
                            badge={t("fragments_badge_streak")}
                            badgeColor="blue"
                            shopImage="/images/site/assets/shop/shop-shield.jpg"
                            onInfoClick={openProductImageModal}
                        />
                    </section>

                    {/* Progression Section */}
                    <section className="mb-6 sm:mb-8">
                        <SectionTitle icon="graph-up-bold-duotone">{t("fragments_section_progress")}</SectionTitle>
                        <ProductCard
                            icon="/static/icons/boost.svg"
                            title={t("fragments_shop_booster_title")}
                            description={t("fragments_shop_booster_description")}
                            price={process.env.SHOP_PRICE_BOOSTER}
                            onBuy={buyBooster}
                            isLoading={isBuying}
                            t={t}
                            formatPrice={formatPrice}
                            badge={t("fragments_badge_xp")}
                            badgeColor="amber"
                            note={
                                activeBooster
                                    ? t("fragments_shop_booster_running")
                                    : boosterInventory.length > 0
                                      ? t("fragments_shop_booster_in_stock", {
                                            count: boosterInventory.length,
                                        })
                                      : null
                            }
                        />
                    </section>

                    {/* Goodies Section */}
                    <section className="mb-6 sm:mb-8">
                        <SectionTitle icon="gift-bold-duotone">{t("fragments_section_goodies")}</SectionTitle>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <ProductCardCompact
                                icon="/static/icons/pen.svg"
                                title={t("fragments_shop_pen_title")}
                                description={t("fragments_shop_pen_description")}
                                price={process.env.SHOP_PRICE_PEN}
                                onBuy={() => buyItem(process.env.SHOP_PRICE_PEN, "/static/icons/pen.svg")}
                                isLoading={isBuying}
                                t={t}
                                formatPrice={formatPrice}
                                shopImage="/images/site/assets/shop/shop-pen.jpg"
                                onInfoClick={openProductImageModal}
                            />
                            <ProductCardCompact
                                icon="/static/icons/booknote.svg"
                                title={t("fragments_shop_booknote_title")}
                                description={t("fragments_shop_booknote_description")}
                                price={process.env.SHOP_PRICE_BOOKNOTE}
                                onBuy={() => buyItem(process.env.SHOP_PRICE_BOOKNOTE, "/static/icons/booknote.svg")}
                                isLoading={isBuying}
                                t={t}
                                formatPrice={formatPrice}
                                shopImage="/images/site/assets/shop/shop-book.jpg"
                                onInfoClick={openProductImageModal}
                            />
                            <ProductCardCompact
                                icon="/static/icons/usb.svg"
                                title={t("fragments_shop_usb_title")}
                                description={t("fragments_shop_usb_description")}
                                price={process.env.SHOP_PRICE_USB}
                                onBuy={() => buyItem(process.env.SHOP_PRICE_USB, "/static/icons/usb.svg")}
                                isLoading={isBuying}
                                t={t}
                                formatPrice={formatPrice}
                                shopImage="/images/site/assets/shop/shop-usb.jpg"
                                onInfoClick={openProductImageModal}
                            />
                            <ProductCardCompact
                                icon="/static/icons/bag.svg"
                                title={t("fragments_shop_bag_title")}
                                description={t("fragments_shop_bag_description")}
                                price={process.env.SHOP_PRICE_BAG}
                                onBuy={() => buyItem(process.env.SHOP_PRICE_BAG, "/static/icons/bag.svg")}
                                isLoading={isBuying}
                                t={t}
                                formatPrice={formatPrice}
                                shopImage="/images/site/assets/shop/shop-bag.jpg"
                                onInfoClick={openProductImageModal}
                            />
                            <ProductCardCompact
                                icon="/static/icons/mug.svg"
                                title={t("fragments_shop_mug_title")}
                                description={t("fragments_shop_mug_description")}
                                price={process.env.SHOP_PRICE_MUG}
                                onBuy={() => buyItem(process.env.SHOP_PRICE_MUG, "/static/icons/mug.svg")}
                                isLoading={isBuying}
                                t={t}
                                formatPrice={formatPrice}
                                shopImage="/images/site/assets/shop/shop-mug.jpg"
                                onInfoClick={openProductImageModal}
                            />
                        </div>
                    </section>

                    {/* Exclusive Section */}
                    <section>
                        <SectionTitle icon="medal-star-bold-duotone">{t("fragments_section_exclusive")}</SectionTitle>
                        <div className="group relative rounded-2xl border border-gray-200 dark:border-bgDark-300 bg-white dark:bg-bgDark-840 p-5 sm:p-6 hover:border-primary dark:hover:border-primary hover:shadow-xl hover:shadow-primary/5 transition-all duration-300 overflow-hidden">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5">
                                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                                    <Image src="/static/icons/theater.svg" width={40} height={40} alt="Invitation" className="w-9 h-9 sm:w-10 sm:h-10 object-contain" />
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="font-bold font-sourceSans text-gray-900 dark:text-white text-base sm:text-lg">
                                            {t("fragments_shop_invitation_title")}
                                        </h3>
                                        <span className="px-2 py-0.5 rounded-md text-[10px] sm:text-xs font-bold uppercase bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400">
                                            {t("fragments_badge_exclusive")}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-0">
                                        {t("fragments_shop_invitation_description")}
                                    </p>
                                </div>

                                <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0">
                                    <span className="text-xs sm:text-sm font-medium text-amber-600 dark:text-amber-400">
                                        {t("fragments_shop_invitation_forTheMostLoyal")}
                                    </span>
                                    <button
                                        onClick={() => setIsInvitationOpen(true)}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 !text-white rounded-full font-semibold text-sm btn-3d lip-orange transition-all duration-200"
                                    >
                                        {t("fragments_shop_invitation_button")}
                                    </button>
                                </div>
                            </div>

                            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 to-amber-600 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
                        </div>
                    </section>
                </div>

                {/* Sidebar */}
                <div className="xl:w-80">
                    <div className="xl:sticky xl:top-24">
                        <div className="rounded-2xl border border-gray-200 dark:border-bgDark-300 bg-white dark:bg-bgDark-840 p-5 sm:p-6">
                            {session ? (
                                <>
                                    <div className="flex items-center gap-3 mb-4">
                                        <div className="w-11 h-11 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                                            <Image src="/static/icons/fragment.svg" width={22} height={22} alt="" className="w-5 h-5" />
                                        </div>
                                        <h3 className="font-bold font-sourceSans text-gray-900 dark:text-white text-base sm:text-lg leading-tight mb-0">
                                            {t("fragments_shop_your_fragments")}
                                        </h3>
                                    </div>
                                    <Fragments session={session} withoutPopup />
                                </>
                            ) : (
                                <div className="text-center">
                                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                        <Image src="/static/icons/fragment.svg" width={32} height={32} alt="Fragment" className="w-8 h-8" />
                                    </div>
                                    <h3 className="font-bold font-sourceSans text-gray-900 dark:text-white text-base sm:text-lg mb-1">
                                        {t("fragments_shop_login_to_buy")}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-5">
                                        {t("fragments_shop_login_to_buy_description")}
                                    </p>
                                    <Link
                                        href="/login"
                                        className="inline-flex items-center justify-center gap-2 w-full px-5 py-2.5 bg-primary !text-white rounded-full font-semibold text-sm btn-3d lip-orange transition-all duration-200"
                                    >
                                        {t("fragments_shop_login_to_buy_button")}
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
